import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { PlanBadge } from '../../components/ui/Badge'
import { getUserFromToken } from '../../lib/auth'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due']

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

export function BillingSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { loadUser, user } = useAuthStore()
  const { org, fetchOrg, fetchUsage } = useOrgStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const sessionId = searchParams.get('session_id')
  const baselinePlan = useMemo(() => getUserFromToken()?.plan || 'free', [])

  useEffect(() => {
    let cancelled = false

    async function syncBillingState() {
      setLoading(true)
      setError('')

      try {
        let orgData = null

        for (let attempt = 0; attempt < 5; attempt += 1) {
          orgData = await fetchOrg()
          await fetchUsage()

          const currentPlan = orgData?.org?.plan || baselinePlan
          const subscriptionStatus = orgData?.org?.subscriptionStatus
          const synced =
            currentPlan !== baselinePlan ||
            (baselinePlan === 'free' &&
              ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus))

          if (synced || !sessionId) {
            break
          }

          await delay(1500)
        }

        await loadUser()

        if (!cancelled && !orgData?.org) {
          setError('We could not confirm the updated subscription yet. Please refresh Billing in a moment.')
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'We could not confirm the updated subscription yet.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    syncBillingState()

    return () => {
      cancelled = true
    }
  }, [baselinePlan, fetchOrg, fetchUsage, loadUser, sessionId])

  const currentPlan = org?.plan || user?.plan || baselinePlan

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-8 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
          {loading ? (
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          ) : (
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          )}
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Upgrade successful
        </h1>
        <p className="text-gray-500 mb-6">
          {loading
            ? 'We are confirming your latest subscription details with Stripe.'
            : 'Your subscription is active and your account has been updated.'}
        </p>

        <div className="bg-gray-50 rounded-2xl p-5 mb-6">
          <p className="text-sm text-gray-500 mb-2">Current plan</p>
          <div className="flex items-center justify-center gap-3">
            <PlanBadge plan={currentPlan} size="lg" />
            {org?.subscriptionStatus && (
              <span className="text-sm text-gray-500 capitalize">
                {org.subscriptionStatus}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl px-4 py-3 text-sm text-yellow-800 mb-6">
            {error}
          </div>
        )}

        <div className="flex justify-center">
          <Button
            variant="primary"
            onClick={() => navigate('/dashboard')}
            icon={<ArrowRight className="w-4 h-4" />}
            iconPosition="right"
          >
            Back to dashboard
          </Button>
        </div>
      </motion.div>
    </div>
  )
}
